import { Method } from 'axios'
import Cookies from 'js-cookie'

import { API_TYPES, COMMON_NR_EVENTS } from '../_constants/logs'
import { TRACE_ID_KEY } from '../_constants/header'

import { createTraceId } from './helpers'
import { STATUS_CODE } from '@/root/constants/api-status-codes'
import { getApiGatewayUrl } from '@/root/utils/network-adapter/api-gateway'
import { logNewRelicEvent } from '@/root/utils/common/custom/newrelic-page-events'

interface CommonFetchProps {
  apiType?: (typeof API_TYPES)[keyof typeof API_TYPES]
}

export const getHeaders = () => {
  const token = Cookies.get('token')
  const traceId = Cookies.get('trace_id')

  return {
    ...(token && { Authorization: `Bearer ${token}` }),
    platform: 'web',
    'Content-Type': 'application/json',
    ...(traceId && { [TRACE_ID_KEY]: createTraceId(traceId) }),
  }
}

export const getFetchRequest = async ({
  url = '',
  config,
  timeout = 0,
  apiType = API_TYPES.unspecified,
}: {
  url: RequestInfo
  config?: RequestInit
  /** @default 0 (timeout disabled) */
  timeout?: number
} & CommonFetchProps) => {
  const requestTraceId = config?.headers?.[TRACE_ID_KEY] ?? 'unset'

  const startTime = Date.now()
  const apiGatewayUrl = getApiGatewayUrl(url.toString())
  const controller =
    typeof window === 'undefined' ? undefined : new window.AbortController()
  const { signal } = controller ?? {}
  let timer
  if (timeout) {
    timer = setTimeout(() => {
      controller?.abort()

      clearTimeout(timer)
    }, timeout)
  }
  try {
    const res = await fetch(apiGatewayUrl, { ...config, signal }).finally(
      () => {
        logNewRelicEvent(COMMON_NR_EVENTS.indStocksApiRequest, {
          url: apiGatewayUrl,
          method: config?.method ?? 'GET',
          responseTime: Date.now() - startTime,
          apiType,
          traceId: requestTraceId,
        })
      },
    )
    if (res.ok) {
      if (timeout) {
        clearTimeout(timer)
      }
      const response = await res.json()
      return response
    } else {
      const response = await res.json()
      if (timeout) {
        clearTimeout(timer)
      }
      const err = {
        apiResponse: res,
        statusCode: res.status,
        url: res.url,
      }
      logNewRelicEvent(COMMON_NR_EVENTS.indStocksFetchError, {
        error: JSON.stringify(err),
        statusCode: err.statusCode.toString(),
        url: err.url,
        apiType,
        traceId: requestTraceId,
      })

      // Status 424 is for 2FA journey
      if (
        res.status === STATUS_CODE[424] &&
        typeof window === 'object' &&
        !window.location.pathname.includes('journey/2fa')
      ) {
        if (response?.data?.navlink?.web) {
          const url = new URL(response.data?.navlink.web)

          const search = new URLSearchParams(url.search)

          const currUrl = new URL(window.location.href)

          search.append(
            'return_to',
            encodeURIComponent(currUrl.pathname + currUrl.search).substring(
              0,
              1000,
            ),
          )

          window.location.href = `${url.pathname}?${search.toString()}`
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw err
    }
  } catch (err: unknown) {
    if (timeout) {
      clearTimeout(timer)
    }

    logNewRelicEvent(COMMON_NR_EVENTS.indStocksFetchThrownError, {
      error: JSON.stringify({
        message: err instanceof Error ? err.message : String(err),
        name: err instanceof Error ? err.name : 'unknown',
        stack: err instanceof Error ? err.stack : 'unknown',
      }),
      statusCode:
        'statusCode' in (err as object)
          ? (err as { statusCode?: number }).statusCode?.toString() ?? ''
          : '',
      url: apiGatewayUrl,
      apiType,
      traceId: requestTraceId,
    })

    throw err
  }
}

export const fetchPOSTRequest = async ({
  method = 'POST',
  url,
  headers,
  data,
  apiType = API_TYPES.unspecified,
}: {
  method?: Method | undefined
  url: string
  headers: HeadersInit
  data?: object
} & CommonFetchProps) => {
  const requestTraceId = headers?.[TRACE_ID_KEY] ?? 'unset'
  const startTime = Date.now()
  const apiGatewayUrl = getApiGatewayUrl(url)

  // eslint-disable-next-line no-useless-catch
  try {
    const resp = await fetch(apiGatewayUrl, {
      method,
      headers,
      ...(data && { body: JSON.stringify(data) }),
    }).finally(() => {
      logNewRelicEvent(COMMON_NR_EVENTS.indStocksApiRequest, {
        url: apiGatewayUrl,
        method,
        responseTime: Date.now() - startTime,
        apiType,
        traceId: requestTraceId,
      })
    })
    const response = await resp.json()
    if (resp.ok) return response
    else {
      const errorResp = { statusCode: resp.status }
      const errors = {
        // don't need an 'error' key because it's just an empty error
        error: new Error(),
        ...response,
        ...errorResp,
      }

      logNewRelicEvent(COMMON_NR_EVENTS.indStocksPostFetchError, {
        error: JSON.stringify(response),
        statusCode: errorResp.statusCode.toString(),
        url: apiGatewayUrl,
        apiType,
        traceId: requestTraceId,
      })

      console.info(`${method} API FAILED, ${apiGatewayUrl}`, errors)
      // STATUS CODE 401 is for unauthorised user
      if (
        resp.status === STATUS_CODE[401] &&
        typeof window === 'object' &&
        window.location.pathname !== '/login'
      ) {
        // Temperory handling for logout page with 401 status code
        if (window.location.pathname.includes('logout')) {
          return response
        }
        window.location.href = '/logout'
      }
      throw errors // eslint-disable-line
    }
  } catch (err: unknown) {
    logNewRelicEvent(COMMON_NR_EVENTS.indStocksPostThrownFetchError, {
      error: JSON.stringify({
        message: err instanceof Error ? err.message : String(err),
        name: err instanceof Error ? err.name : 'unknown',
        stack: err instanceof Error ? err.stack : 'unknown',
      }),
      statusCode:
        'statusCode' in (err as object)
          ? (err as { statusCode?: number }).statusCode?.toString() ?? ''
          : '',
      url: apiGatewayUrl,
      apiType,
      traceId: requestTraceId,
    })
    throw err // eslint-disable-line
  }
}

export const fetcher = async ({
  url,
  config,
  timeout,
  apiType,
}: {
  url: RequestInfo
  config?: RequestInit
  timeout?: number
} & CommonFetchProps) => {
  return getFetchRequest({ url, config, timeout, apiType })
}
